import { useState } from "react";
import { ChevronDown, ExternalLink, Briefcase, Loader2 } from "lucide-react";

const statusStyle = {
  evaluated: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  applied: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  skipped: "bg-slate-800 text-slate-400 border-slate-700",
  rejected: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  interview: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  offer: "bg-emerald-400/20 text-emerald-300 border-emerald-400/40",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

function ClientGroup({ client, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const evals = client.evaluations || [];
  return (
    <div data-testid={`breakdown-client-${client.id}`} className="bg-[#12151F] border border-slate-800/80 rounded-xl overflow-hidden">
      <button
        data-testid={`breakdown-toggle-${client.id}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-800/30 transition-colors text-left"
      >
        <div className="w-8 h-8 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-sm font-bold">
          {client.name?.[0] || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-100 truncate flex items-center gap-2">
            {client.name}
            {client.archived && <span className="text-[10px] uppercase tracking-wide text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">archived</span>}
          </div>
          <div className="text-[11px] text-slate-500">
            {evals.length} job{evals.length === 1 ? "" : "s"} analysed
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${client.profile_ready ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>
          {client.profile_ready ? "Profile ready" : "No profile"}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-slate-800/60">
          {evals.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500 text-center">No jobs analysed yet for this client.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0D0E15] text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    {["Job analysed", "Conclusion", "Fit score", "Status", "Date"].map((h) => (
                      <th key={h} className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {evals.map((e) => (
                    <tr key={e.id} data-testid={`breakdown-eval-${e.id}`} className="hover:bg-slate-800/25 transition-colors">
                      <td className="px-4 py-3 min-w-[220px]">
                        <div className="text-slate-200 font-medium flex items-center gap-1.5">
                          {e.job_title || "—"}
                          {e.job_url && (
                            <a href={e.job_url} target="_blank" rel="noreferrer" data-testid={`breakdown-joburl-${e.id}`} className="text-indigo-400 hover:text-indigo-300">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{e.company || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={e.decision === "APPLY"
                          ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase whitespace-nowrap"
                          : "bg-rose-500/15 text-rose-400 border border-rose-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase whitespace-nowrap"}>
                          {e.decision || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-200">{e.fit_score ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs border rounded-md px-2 py-1 capitalize ${statusStyle[e.status] || statusStyle.evaluated}`}>
                          {e.status || "evaluated"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClientJobBreakdown({ clients, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 text-sm gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!clients || clients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-sm gap-2">
        <Briefcase className="w-6 h-6 text-slate-600" />
        No clients assigned yet.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {clients.map((c, i) => (
        <ClientGroup key={c.id} client={c} defaultOpen={clients.length === 1 || i === 0} />
      ))}
    </div>
  );
}
